import {
  Drawer,
  useExpandedRowKeysContext,
  useTableRowSelection,
} from '@/components';
import { useExpenditureBudgetPurpose } from '@/features/master-table/hooks';
import { useProject } from '@/features/project/_hooks/project';
import { useCreateSubProject } from '@/features/project/_hooks/sub-project';
import { checkEnabledInputReflectedAmount } from '@/features/project/_utils/inspection-evaluation';
import { InspectionEvaluationRsModel } from '@/features/project/projects/_models/project';
import {
  useCreateExpenditureCart,
  useUpdateExpenditureCart,
} from '@/features/project/projects/detail/income-and-expenditure/_hooks/expenditure-cart';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import type { SubProjectsModel } from '../../../../_models/sub-project';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { usePreviewRowState } from '../shared/PreviewRowStateProvider';
import { ExpenditureCartForm } from './ExpenditureCartForm';

type Props = {
  data: z.infer<typeof SubProjectsModel.element> | undefined;
};

export const ExpenditureCartFormDrawer: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const { openMessage } = useMessage();
  const project = useProject(projectId);
  const { data: majorExpenditureBudgetPurposeData } =
    useExpenditureBudgetPurpose({
      id: project.data?.majorExpenditureBudgetPurposeId ?? '',
    });
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { setPreviewRowState } = usePreviewRowState();

  const { mutateAsync: createSubProjectAsync } = useCreateSubProject();
  const { mutateAsync: createExpenditureCartAsync } =
    useCreateExpenditureCart();
  const { mutateAsync: updateExpenditureCartAsync } =
    useUpdateExpenditureCart();
  const { clearAllSelections } = useTableRowSelection();
  const { addExpandedRowKeys } = useExpandedRowKeysContext();

  const openDrawer =
    drawerState.open && drawerState.type === 'expenditure-cart';
  const expenditureCart =
    openDrawer && drawerState.isEditing
      ? data?.expenditureCarts.find(
          (d) => d.id === drawerState.expenditureCartId,
        )
      : undefined;

  return (
    <Drawer
      open={openDrawer}
      onClose={() => {
        setDrawerState({ open: false });
        clearAllSelections();
      }}
      destroyOnClose
    >
      <ExpenditureCartForm
        fiscalYear={project.data?.fiscalYear}
        isSkipAdministrativeReformTeamEvaluation={
          project.data?.sheetType === 'RS' &&
          project.data.skipAdministrativeReformTeamEvaluation
        }
        initialValues={{
          accountingId: majorExpenditureBudgetPurposeData?.accountingId,
          accountTitleId: majorExpenditureBudgetPurposeData?.accountTitleId,
          ...expenditureCart,
        }}
        type={
          project.data?.majorExpenditureBudgetPurposeId
            ? 'review-skipped'
            : 'basic'
        }
        onSubmit={async ({ values, nextDrawerKey, onSuccess }) => {
          invariant(openDrawer, t('unreachable-code'));

          if (drawerState.isEditing && data) {
            await updateExpenditureCartAsync({
              ...values,
              id: drawerState.expenditureCartId,
              projectId,
              subProjectId: data.id,
            });

            const inspectionEvaluation =
              InspectionEvaluationRsModel.partial().safeParse(
                project.data?.inspectionEvaluation,
              );

            const enabledInputReflectedAmount = inspectionEvaluation.success
              ? checkEnabledInputReflectedAmount(
                  inspectionEvaluation.data.reflectionStatusBasedOnOpinion
                    ?.action,
                )
              : undefined;
            const isChangeAccountTitleIdOrAccountingId =
              expenditureCart?.accountingId !== values.accountingId ||
              expenditureCart.accountTitleId !== values.accountTitleId;

            enabledInputReflectedAmount && isChangeAccountTitleIdOrAccountingId
              ? await openMessage(
                  'warning',
                  t('warning-message-action-completed-expenditure-cart', {
                    action: t('update'),
                  }),
                )
              : await openMessage('success', t('message-update-completed'));
            setDrawerState({ open: false });
          } else {
            let subProjectId: string;
            if (data) {
              subProjectId = data.id;
            } else {
              const res = await createSubProjectAsync({
                projectId,
                name: '-',
              });
              subProjectId = res.id;
            }
            const res = await createExpenditureCartAsync({
              ...values,
              projectId,
              subProjectId,
            });
            await Promise.all([
              await openMessage('success', t('add-completed')),
              (async () => {
                switch (nextDrawerKey) {
                  case 'expenditure-item':
                    setDrawerState({
                      open: true,
                      type: nextDrawerKey,
                      isEditing: false,
                      subProjectId,
                      expenditureCartId: res.id,
                    });
                    setPreviewRowState({
                      type: nextDrawerKey,
                      expenditureCartId: res.id,
                    });
                    break;

                  case 'expenditure-cart':
                    setPreviewRowState({
                      type: nextDrawerKey,
                      accountingType: undefined,
                    });
                    break;

                  default:
                    setDrawerState({ open: false });
                    break;
                }

                addExpandedRowKeys([res.id]);
              })(),
              (async () => {
                onSuccess?.();
              })(),
            ]);
          }
        }}
        isEditing={
          drawerState.open &&
          drawerState.type === 'expenditure-cart' &&
          drawerState.isEditing
        }
      />
    </Drawer>
  );
};
